// Chiptune music and sound effects for nut mode, synthesised with Web Audio so
// there are no files to load. Starts muted; the audio context is only created
// once the player turns sound on (which is always a click or key press).

const NOTE = (n) => 440 * Math.pow(2, (n - 69) / 12) // MIDI note to Hz

// A backing track for each biome (and for rain and storms), looping in 8th
// notes: lead and bass are MIDI notes per step (0 = rest). leadLength and
// bassLength are how many steps each note rings for.
const TRACKS = {
  // Bright and bouncy, in A minor
  meadow: {
    bpm: 132,
    lead: [
      76, 0, 79, 76, 74, 0, 72, 0, 74, 0, 76, 0, 69, 0, 0, 0,
      72, 0, 74, 72, 71, 0, 67, 0, 69, 0, 71, 72, 74, 0, 0, 0,
      76, 0, 79, 76, 74, 0, 72, 0, 74, 0, 76, 0, 81, 0, 79, 0,
      77, 0, 76, 74, 72, 0, 71, 0, 69, 0, 0, 0, 0, 0, 0, 0,
    ],
    bass: [45, 0, 45, 52, 41, 0, 41, 48, 43, 0, 43, 50, 45, 0, 52, 0],
    leadWave: 'square', leadVolume: 0.09, leadLength: 0.9,
    bassWave: 'triangle', bassVolume: 0.3, bassLength: 1.6,
  },
  // Slow and eerie, long notes drifting over a low drone, in D minor
  fog: {
    bpm: 84,
    lead: [
      62, 0, 0, 0, 65, 0, 0, 0, 69, 0, 0, 0, 67, 0, 65, 0,
      62, 0, 0, 0, 60, 0, 0, 0, 62, 0, 0, 0, 0, 0, 0, 0,
      69, 0, 0, 0, 70, 0, 0, 0, 69, 0, 67, 0, 65, 0, 0, 0,
      64, 0, 0, 0, 65, 0, 0, 0, 62, 0, 0, 0, 0, 0, 0, 0,
    ],
    bass: [38, 0, 0, 0, 0, 0, 0, 0, 34, 0, 0, 0, 0, 0, 0, 0],
    leadWave: 'sine', leadVolume: 0.14, leadLength: 3.5,
    bassWave: 'triangle', bassVolume: 0.32, bassLength: 7.5,
  },
  // Gentle and mellow, like raindrops on a window, in C pentatonic
  rain: {
    bpm: 100,
    lead: [
      72, 0, 76, 0, 79, 0, 76, 0, 74, 0, 72, 0, 69, 0, 0, 0,
      72, 0, 76, 0, 81, 0, 79, 0, 76, 0, 74, 0, 72, 0, 0, 0,
    ],
    bass: [48, 0, 55, 0, 52, 0, 55, 0, 45, 0, 52, 0, 48, 0, 52, 0],
    leadWave: 'triangle', leadVolume: 0.2, leadLength: 1.4,
    bassWave: 'sine', bassVolume: 0.3, bassLength: 1.8,
  },
  // Driving and tense, a pulsing bass under sharp stabs, in E minor
  storm: {
    bpm: 150,
    lead: [
      64, 0, 0, 64, 67, 0, 64, 0, 71, 0, 69, 0, 67, 0, 66, 0,
      64, 0, 0, 64, 67, 0, 71, 0, 74, 0, 72, 0, 71, 0, 0, 0,
    ],
    bass: [40, 40, 40, 40, 40, 40, 40, 40, 36, 36, 36, 36, 38, 38, 38, 38],
    leadWave: 'square', leadVolume: 0.08, leadLength: 0.7,
    bassWave: 'square', bassVolume: 0.07, bassLength: 0.6,
  },
  // Tropical and plucky, a marimba-ish lead over a bouncing bass, in G
  jungle: {
    bpm: 120,
    lead: [
      79, 0, 76, 79, 81, 0, 79, 0, 76, 0, 74, 76, 79, 0, 0, 0,
      74, 0, 76, 0, 79, 0, 81, 83, 84, 0, 81, 0, 79, 0, 0, 0,
    ],
    bass: [43, 0, 50, 0, 43, 43, 50, 0, 41, 0, 48, 0, 41, 41, 48, 0],
    leadWave: 'triangle', leadVolume: 0.22, leadLength: 0.45,
    bassWave: 'triangle', bassVolume: 0.32, bassLength: 0.9,
  },
  // Dusty and exotic, a snake-charmer lead over a slow lope, in E Phrygian dominant
  desert: {
    bpm: 108,
    lead: [
      76, 77, 80, 0, 81, 0, 80, 77, 76, 0, 0, 0, 74, 0, 76, 0,
      77, 0, 80, 81, 83, 0, 81, 80, 77, 0, 76, 0, 0, 0, 0, 0,
    ],
    bass: [40, 0, 0, 47, 40, 0, 41, 0, 40, 0, 0, 47, 44, 0, 41, 0],
    leadWave: 'square', leadVolume: 0.06, leadLength: 0.9,
    bassWave: 'triangle', bassVolume: 0.32, bassLength: 1.4,
  },
  // Sunny calypso, steel-drum plinks over a skipping bass, in F
  tropical: {
    bpm: 126,
    lead: [
      77, 0, 81, 84, 0, 81, 84, 0, 86, 0, 84, 0, 81, 0, 79, 0,
      77, 0, 81, 84, 0, 86, 89, 0, 88, 0, 86, 0, 84, 0, 0, 0,
    ],
    bass: [41, 0, 0, 41, 48, 0, 41, 0, 46, 0, 0, 46, 48, 0, 43, 0],
    leadWave: 'triangle', leadVolume: 0.22, leadLength: 0.5,
    bassWave: 'triangle', bassVolume: 0.32, bassLength: 0.9,
  },
  // Boss fights: fast, heavy and urgent, in C minor
  boss: {
    bpm: 168,
    lead: [
      72, 0, 75, 0, 72, 0, 79, 0, 78, 0, 75, 0, 72, 0, 70, 0,
      72, 0, 75, 0, 72, 0, 80, 79, 78, 0, 75, 0, 74, 0, 0, 0,
    ],
    bass: [36, 36, 48, 36, 36, 36, 46, 36, 32, 32, 44, 32, 34, 34, 46, 34],
    leadWave: 'sawtooth', leadVolume: 0.06, leadLength: 0.8,
    bassWave: 'square', bassVolume: 0.08, bassLength: 0.8,
  },
}

// Boss fights: each kind of boss has its own riff, and each biome gives it its
// own key, tempo, sound and drums, so no two fights sound the same. Riffs are
// semitones from the root per 8th note (_ = rest); drums are k kick, s snare,
// h hat, t tom, . nothing.
const _ = null
const BOSS_RIFFS = {
  // Creepy chromatic trills that skitter up and down
  spider: {
    root: 64, bpm: 160, wave: 'sawtooth',
    lead: [0, 1, 0, 1, 3, _, 1, _, 0, _, -1, _, 0, _, _, _, 7, 8, 7, 8, 10, _, 8, _, 7, _, 6, _, 3, _, 1, _],
    bass: [-24, -24, -12, -24, -23, -23, -11, -23, -24, -24, -12, -24, -20, -20, -8, -20],
    drums: 'k.h.s.h.k.k.s.hh',
  },
  // A galloping hunt
  wolf: {
    root: 62, bpm: 176, wave: 'square',
    lead: [0, _, 0, 0, 3, _, 5, _, 7, _, 7, 7, 8, _, 7, _, 5, _, 5, 5, 3, _, 2, _, 0, _, -2, _, 0, _, _, _],
    bass: [-24, _, -24, -24, -24, _, -24, -24, -16, _, -16, -16, -14, _, -14, -14],
    drums: 'k.kks.k.k.kks.kk',
  },
  // Big bloopy octave leaps
  frog: {
    root: 67, bpm: 150, wave: 'triangle',
    lead: [0, _, 12, _, 10, _, 7, _, 0, _, 3, 5, 7, _, _, _, 0, _, 12, _, 15, _, 14, _, 10, _, 7, _, 5, _, 3, _],
    bass: [-24, _, -12, _, -24, _, -12, _, -26, _, -14, _, -21, _, -9, _],
    drums: 'k..sk.s.k..sk.ss',
  },
  // A serpentine snake-charmer line
  snake: {
    root: 61, bpm: 156, wave: 'sawtooth',
    lead: [0, 1, 4, _, 5, _, 4, 1, 0, _, _, _, 7, 8, _, 7, 5, _, 4, _, 5, 7, 8, _, 7, _, 5, 4, 1, _, 0, _],
    bass: [-24, _, _, -24, -23, _, -24, _, -24, _, _, -24, -17, _, -19, _],
    drums: 'k.h.s.hkk.h.s.hh',
  },
  // Stabbing harmonic minor, fast and dry
  scorpion: {
    root: 64, bpm: 172, wave: 'square',
    lead: [0, _, 3, _, 4, _, 3, 0, -1, _, 0, _, 3, _, _, _, 7, _, 8, _, 11, _, 8, 7, 4, _, 3, _, 0, _, _, _],
    bass: [-24, -24, _, -24, -24, _, -20, _, -19, -19, _, -19, -17, _, -13, _],
    drums: 'kh.hs.hkkh.hs.hh',
  },
  // War drums and a stomping pentatonic call
  iguana: {
    root: 65, bpm: 164, wave: 'square',
    lead: [0, _, 3, 5, _, 7, _, 5, 3, _, 0, _, 10, _, 7, _, 0, _, 3, 5, _, 7, _, 10, 12, _, 10, _, 7, _, _, _],
    bass: [-24, _, -24, _, -21, _, -19, _, -24, _, -24, _, -26, _, -17, _],
    drums: 'ktsk.ks.ktskk.ss',
  },
  // A slow, heavy, swampy crawl
  croc: {
    root: 65, bpm: 140, wave: 'sawtooth',
    lead: [0, _, _, _, 1, _, 0, _, -2, _, _, _, 0, _, _, _, 3, _, _, _, 1, _, 0, _, -2, _, -4, _, -5, _, _, _],
    bass: [-24, -24, _, -24, _, -24, -23, _, -24, -24, _, -24, -26, _, -28, _],
    drums: 'k...s..kk.k.s...',
  },
}
// shift: key change; tempo: speed; wave: lead sound; hats: hats on every
// other step; echo: the lead repeated an octave down a step behind
const BOSS_BIOMES = {
  meadow: { shift: 0, tempo: 1 },
  fog: { shift: -3, tempo: 0.94, echo: true },
  rain: { shift: -1, tempo: 1, echo: true },
  storm: { shift: 1, tempo: 1.08, hats: true },
  jungle: { shift: 2, tempo: 1.03, wave: 'triangle', hats: true },
  desert: { shift: 3, tempo: 0.97, wave: 'square' },
  tropical: { shift: 5, tempo: 1.05, wave: 'triangle', echo: true },
}
const LEAD_VOLUME = { sawtooth: 0.055, square: 0.07, triangle: 0.2, sine: 0.2 }
const bossTrack = (kind, biome) => {
  const riff = BOSS_RIFFS[kind] || BOSS_RIFFS.spider
  const flavour = BOSS_BIOMES[biome] || BOSS_BIOMES.meadow
  const root = riff.root + flavour.shift
  const wave = flavour.wave || riff.wave
  return {
    bpm: Math.round(riff.bpm * flavour.tempo),
    lead: riff.lead.map((n) => (n === null ? 0 : root + n)),
    bass: riff.bass.map((n) => (n === null ? 0 : root + n)),
    drums: flavour.hats ? riff.drums.replace(/\./g, (m, i) => (i % 2 ? 'h' : '.')) : riff.drums,
    echo: !!flavour.echo,
    leadWave: wave, leadVolume: LEAD_VOLUME[wave], leadLength: 0.8,
    bassWave: kind === 'croc' ? 'sawtooth' : 'square', bassVolume: 0.08, bassLength: 0.8,
  }
}
const trackFor = (name) => {
  if (!TRACKS[name] && /^boss:/.test(name)) {
    const [, kind, biome] = name.split(':')
    TRACKS[name] = bossTrack(kind, biome)
  }
  return TRACKS[name]
}

// While a super nut lasts: a quick rising arpeggio over whatever's playing
const SUPER = [81, 84, 88, 93, 79, 83, 86, 91, 77, 81, 84, 89, 76, 79, 83, 88]

const LOOKAHEAD = 0.12
const FADE = 2.5 // seconds to crossfade from one track to the next

export const createSound = () => {
  let ctx = null
  let master = null
  let musicGain = null
  let noise = null
  let on = false
  let playing = false
  let mode = 'normal'
  let timer = null
  let trackName = 'meadow'
  // Tracks playing now: the current one, and any still fading out
  let voices = []

  const setup = () => {
    if (ctx) return
    const Ctx = window.AudioContext || window.webkitAudioContext
    if (!Ctx) return
    ctx = new Ctx()
    master = ctx.createGain()
    master.gain.value = 0.5
    master.connect(ctx.destination)
    musicGain = ctx.createGain()
    musicGain.gain.value = 0.35
    musicGain.connect(master)
    // A second of white noise for crunches, swishes and thuds
    noise = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate)
    const data = noise.getChannelData(0)
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1
  }

  // One shaped note: type, start and end pitch (Hz), length, volume, delay
  const tone = (type, from, to, length, volume, delay = 0, out = master) => {
    const t = ctx.currentTime + delay
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = type
    osc.frequency.setValueAtTime(from, t)
    if (to !== from) osc.frequency.exponentialRampToValueAtTime(to, t + length)
    gain.gain.setValueAtTime(volume, t)
    gain.gain.exponentialRampToValueAtTime(0.001, t + length)
    osc.connect(gain)
    gain.connect(out)
    osc.start(t)
    osc.stop(t + length + 0.02)
  }
  const hiss = (length, volume, filter, freq, delay = 0, out = master) => {
    const t = ctx.currentTime + delay
    const src = ctx.createBufferSource()
    src.buffer = noise
    const shape = ctx.createBiquadFilter()
    shape.type = filter
    shape.frequency.setValueAtTime(freq, t)
    shape.frequency.exponentialRampToValueAtTime(Math.max(40, freq / 4), t + length)
    const gain = ctx.createGain()
    gain.gain.setValueAtTime(volume, t)
    gain.gain.exponentialRampToValueAtTime(0.001, t + length)
    src.connect(shape)
    shape.connect(gain)
    gain.connect(out)
    src.start(t, Math.random() * 0.5)
    src.stop(t + length + 0.02)
  }

  const EFFECTS = {
    jump: () => tone('square', 330, 660, 0.12, 0.12),
    pounce: () => {
      hiss(0.16, 0.25, 'bandpass', 2400)
      tone('square', 520, 260, 0.1, 0.08)
    },
    eat: () => {
      tone('square', NOTE(76), NOTE(76), 0.07, 0.12)
      tone('square', NOTE(83), NOTE(83), 0.12, 0.12, 0.07)
    },
    super: () => [72, 76, 79, 84, 88, 91, 96].forEach((n, i) => tone('square', NOTE(n), NOTE(n), 0.09, 0.1, i * 0.06)),
    heart: () => [72, 76, 79, 84].forEach((n, i) => tone('triangle', NOTE(n), NOTE(n), 0.14, 0.25, i * 0.08)),
    hurt: () => {
      tone('sawtooth', 440, 110, 0.35, 0.16)
      hiss(0.2, 0.2, 'lowpass', 1800)
    },
    poison: () => {
      tone('triangle', 300, 120, 0.5, 0.25)
      tone('triangle', 240, 90, 0.5, 0.18, 0.12)
    },
    hit: () => {
      tone('square', 180, 60, 0.12, 0.18)
      hiss(0.1, 0.3, 'lowpass', 1200)
    },
    kill: () => {
      tone('square', 300, 50, 0.3, 0.16)
      hiss(0.25, 0.25, 'lowpass', 900)
    },
    chop: () => {
      hiss(0.35, 0.4, 'lowpass', 1600)
      tone('square', 140, 70, 0.1, 0.14)
    },
    fell: () => {
      hiss(0.9, 0.45, 'lowpass', 1400)
      tone('triangle', 120, 40, 0.8, 0.35)
    },
    smash: () => {
      tone('square', 600, 900, 0.05, 0.1)
      hiss(0.18, 0.3, 'highpass', 3000)
    },
    // A quick deflating squeak and a soft bump
    over: () => {
      tone('sine', 900, 180, 0.28, 0.07)
      tone('square', 110, 55, 0.12, 0.05, 0.26)
    },
    // Rolling thunder, a little after the flash
    thunder: (delay = 0) => {
      hiss(2.2, 0.55, 'lowpass', 420, delay)
      hiss(1.2, 0.35, 'lowpass', 900, delay + 0.05)
      tone('sine', 55, 32, 1.6, 0.25, delay)
    },
    // A wet spit, and the crackle of a creature charged by lightning
    spit: () => {
      tone('sine', 700, 220, 0.12, 0.12)
      hiss(0.08, 0.15, 'bandpass', 2200)
    },
    zap: () => {
      hiss(0.25, 0.35, 'highpass', 4000)
      tone('square', 1400, 300, 0.2, 0.06)
    },
    // Picking up a power-up: a bright rising sparkle
    power: () => [79, 83, 86, 91, 95].forEach((n, i) => tone('triangle', NOTE(n), NOTE(n), 0.1, 0.2, i * 0.045)),
    // A pounce bouncing off a boss that isn't open to attack, or a shield blocking
    clank: () => {
      tone('square', 1200, 900, 0.08, 0.1)
      tone('square', 1800, 1500, 0.06, 0.06, 0.03)
    },
    // A boss's roar as the arena closes, and its crash into a wall
    roar: () => {
      tone('sawtooth', 120, 60, 0.9, 0.2)
      hiss(0.8, 0.3, 'lowpass', 700)
    },
    crash: () => {
      hiss(0.5, 0.5, 'lowpass', 900)
      tone('square', 90, 40, 0.4, 0.2)
    },
    // A shockwave rolling out from a landing
    quake: () => {
      tone('sine', 70, 35, 0.7, 0.4)
      hiss(0.6, 0.3, 'lowpass', 500)
    },
    // A ground warning before lightning strikes nearby
    warn: () => {
      tone('square', 880, 880, 0.08, 0.05)
      tone('square', 880, 880, 0.08, 0.05, 0.16)
    },
    // A creature waking up
    alert: () => tone('square', 600, 1200, 0.1, 0.06),
    victory: () => [72, 76, 79, 84, 79, 84].forEach((n, i) => tone('square', NOTE(n), NOTE(n), 0.16, 0.1, i * 0.13)),
    munch: () => {
      hiss(0.08, 0.2, 'bandpass', 1800)
      hiss(0.08, 0.2, 'bandpass', 1400, 0.09)
    },
  }

  // Each playing track has its own gain, so one can fade in as another fades out
  const startVoice = (name, fadeIn) => {
    const gain = ctx.createGain()
    gain.connect(musicGain)
    const now = ctx.currentTime
    gain.gain.setValueAtTime(fadeIn ? 0.0001 : 1, now)
    if (fadeIn) gain.gain.exponentialRampToValueAtTime(1, now + FADE)
    voices.push({ name, track: trackFor(name), gain, step: 0, nextAt: now + 0.05, endsAt: Infinity })
  }
  const fadeOut = (voice) => {
    const now = ctx.currentTime
    voice.gain.gain.cancelScheduledValues(now)
    voice.gain.gain.setValueAtTime(Math.max(0.0001, voice.gain.gain.value), now)
    voice.gain.gain.exponentialRampToValueAtTime(0.0001, now + FADE)
    voice.endsAt = now + FADE
  }

  // Music is scheduled a little ahead on a timer, so it keeps time while the frame rate dips
  const schedule = () => {
    const now = ctx.currentTime
    voices = voices.filter((v) => {
      if (v.endsAt > now) return true
      v.gain.disconnect()
      return false
    })
    voices.forEach((v) => {
      const t = v.track
      const stepTime = 60 / t.bpm / 2
      while (v.nextAt < now + LOOKAHEAD && v.nextAt < v.endsAt) {
        const lead = mode === 'super' ? SUPER[v.step % SUPER.length] : t.lead[v.step % t.lead.length]
        const bass = t.bass[v.step % t.bass.length]
        const delay = Math.max(0, v.nextAt - now)
        if (lead) {
          const length = mode === 'super' ? 0.9 : t.leadLength
          tone(mode === 'super' ? 'square' : t.leadWave, NOTE(lead), NOTE(lead), stepTime * length, mode === 'super' ? 0.09 : t.leadVolume, delay, v.gain)
        }
        if (bass) tone(t.bassWave, NOTE(bass), NOTE(bass), stepTime * t.bassLength, t.bassVolume, delay, v.gain)
        // Boss tracks: drums, and in some biomes an echo of the lead
        const drum = t.drums && t.drums[v.step % t.drums.length]
        if (drum === 'k') tone('sine', 150, 40, 0.18, 0.5, delay, v.gain)
        else if (drum === 's') {
          hiss(0.14, 0.3, 'highpass', 1800, delay, v.gain)
          tone('triangle', 220, 140, 0.08, 0.12, delay, v.gain)
        } else if (drum === 'h') hiss(0.04, 0.1, 'highpass', 7000, delay, v.gain)
        else if (drum === 't') tone('sine', 220, 110, 0.15, 0.3, delay, v.gain)
        const echo = t.echo && mode !== 'super' && t.lead[(v.step + t.lead.length - 1) % t.lead.length]
        if (echo) tone(t.leadWave, NOTE(echo - 12), NOTE(echo - 12), stepTime * t.leadLength, t.leadVolume * 0.45, delay, v.gain)
        v.nextAt += mode === 'super' ? stepTime * 0.75 : stepTime
        v.step++
      }
    })
  }
  const startMusic = () => {
    if (!ctx || timer) return
    startVoice(trackName, false)
    timer = setInterval(schedule, 40)
    schedule()
  }
  const stopMusic = () => {
    clearInterval(timer)
    timer = null
    if (ctx) voices.forEach((v) => v.gain.disconnect())
    voices = []
  }
  // Rain: a soft, steady hiss of filtered noise, as loud as the weather is heavy
  let rainLevel = 0
  let rainGain = null
  const rainLoop = () => {
    if (rainGain || !ctx) return
    const src = ctx.createBufferSource()
    src.buffer = noise
    src.loop = true
    const shape = ctx.createBiquadFilter()
    shape.type = 'bandpass'
    shape.frequency.value = 2600
    shape.Q.value = 0.6
    rainGain = ctx.createGain()
    rainGain.gain.value = 0
    src.connect(shape)
    shape.connect(rainGain)
    rainGain.connect(master)
    src.start()
  }
  const refresh = () => {
    if (on && playing) startMusic()
    else stopMusic()
    if (ctx && (rainLevel > 0.01 || rainGain)) {
      rainLoop()
      rainGain.gain.setTargetAtTime(on ? rainLevel * 0.12 : 0, ctx.currentTime, 0.4)
    }
  }

  return {
    get on() {
      return on
    },
    toggle() {
      on = !on
      if (on) {
        setup()
        if (ctx && ctx.state === 'suspended') ctx.resume()
      }
      refresh()
      return on
    },
    // Music plays only while the game is up and running
    setPlaying(value) {
      playing = value
      refresh()
    },
    setMode(value) {
      mode = value
    },
    // Crossfades to another backing track
    setTrack(name) {
      if (!trackFor(name) || name === trackName) return
      trackName = name
      if (!ctx || !timer) return
      voices.forEach((v) => {
        if (v.endsAt === Infinity) fadeOut(v)
      })
      startVoice(name, true)
    },
    setRain(level) {
      if (Math.abs(level - rainLevel) < 0.02 && !(level === 0 && rainLevel > 0)) return
      rainLevel = level
      refresh()
    },
    play(name, delay) {
      if (on && ctx && EFFECTS[name]) EFFECTS[name](delay)
    },
    dispose() {
      stopMusic()
      if (ctx) ctx.close()
    },
  }
}
